import { ColumnDef } from '@tanstack/react-table'
import { PromotionDetailPlayerData } from '@/modules/dashboard/type'
import { Button } from '@/common/ui/button'
import { SortingArrows } from '@/common/components/SortingArrows'
import { Link } from 'react-router-dom'
import { PhoneCell } from '@/common/components/PhoneCell'

export const columns: ColumnDef<PromotionDetailPlayerData>[] = [
	{
		accessorKey: 'phone',
		header: 'เบอร์โทรศัพท์',
		cell: ({ row }) => (
			<PhoneCell
				playerId={row.original.playerId}
				phone={row.original.phone || ''}
			/>
		),
	},
	{
		accessorKey: 'totalReceiveTime',
		header: ({ column }) => {
			return (
				<Button
					variant="ghost"
					onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
					className="px-0 hover:bg-transparent"
				>
					จำนวนรับทั้งหมด / สถานะเทิร์น
					<SortingArrows sortOrder={column.getIsSorted()} />
				</Button>
			)
		},
		cell: ({ row }) => {
			const { totalReceiveTime, totalTimeFinishTurnover } = row.original
			const notPassedTurnover = totalReceiveTime - totalTimeFinishTurnover

			return (
				<div className="flex flex-col">
					<span className="text-sm font-medium">
						{totalReceiveTime.toLocaleString()} ครั้ง
					</span>
					<div className="mt-1 flex flex-col gap-1">
						<span className="text-xs text-red-500">
							ผ่านเทิร์น: {totalTimeFinishTurnover.toLocaleString()} ครั้ง
						</span>
						<span className="text-xs text-green-500">
							ไม่ผ่านเทิร์น: {notPassedTurnover.toLocaleString()} ครั้ง
						</span>
					</div>
				</div>
			)
		},
	},
	{
		accessorKey: 'totalDepositAmount',
		header: ({ column }) => {
			return (
				<Button
					variant="ghost"
					onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
					className="px-0 hover:bg-transparent"
				>
					ยอดฝากรวม / เฉลี่ย (บาท)
					<SortingArrows sortOrder={column.getIsSorted()} />
				</Button>
			)
		},
		cell: ({ row }) => {
			const { totalDepositAmount, totalReceiveTime } = row.original
			const average =
				totalReceiveTime > 0 ? totalDepositAmount / totalReceiveTime : 0
			return (
				<div className="flex flex-col">
					<span className="text-sm text-green-500">
						฿{totalDepositAmount.toLocaleString()}
					</span>
					<span className="text-xs text-muted-foreground">
						เฉลี่ย: ฿
						{average.toLocaleString(undefined, {
							minimumFractionDigits: 2,
							maximumFractionDigits: 2,
						})}
					</span>
				</div>
			)
		},
	},
	{
		accessorKey: 'totalTransferAmount',
		header: ({ column }) => {
			return (
				<Button
					variant="ghost"
					onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
					className="px-0 hover:bg-transparent"
				>
					ยอดโอนกระเป๋าทั้งหมด / เฉลี่ย (บาท)
					<SortingArrows sortOrder={column.getIsSorted()} />
				</Button>
			)
		},
		cell: ({ row }) => {
			const { totalTransferAmount, totalReceiveTime, totalTimeFinishTurnover } =
				row.original
			const averagePerReceiveTime =
				totalReceiveTime > 0 ? totalTransferAmount / totalReceiveTime : 0
			const averagePerFinishTime =
				totalTimeFinishTurnover > 0
					? totalTransferAmount / totalTimeFinishTurnover
					: 0

			return (
				<div className="flex flex-col">
					<span className="text-sm font-medium">
						฿{totalTransferAmount.toLocaleString()}
					</span>
					<div className="mt-1 flex flex-col gap-1">
						<span className="text-xs text-gray-500">
							เฉลี่ยทั้งหมด: ฿
							{averagePerReceiveTime.toLocaleString(undefined, {
								minimumFractionDigits: 2,
								maximumFractionDigits: 2,
							})}
						</span>
						<span className="text-xs text-gray-500">
							เฉลี่ยหลังทำเทิร์นสำเร็จ: ฿
							{averagePerFinishTime.toLocaleString(undefined, {
								minimumFractionDigits: 2,
								maximumFractionDigits: 2,
							})}
						</span>
					</div>
				</div>
			)
		},
	},
	{
		accessorKey: 'balanceAfterTurnoverFinish',
		header: ({ column }) => {
			return (
				<Button
					variant="ghost"
					onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
					className="px-0 hover:bg-transparent"
				>
					ยอดคงเหลือหลังทำเทิร์นสำเร็จ / เฉลี่ย (บาท)
					<SortingArrows sortOrder={column.getIsSorted()} />
				</Button>
			)
		},
		cell: ({ row }) => {
			const {
				balanceAfterTurnoverFinish,
				totalReceiveTime,
				totalTimeFinishTurnover,
			} = row.original
			const averagePerReceiveTime =
				totalReceiveTime > 0 ? balanceAfterTurnoverFinish / totalReceiveTime : 0
			const averagePerFinishTime =
				totalTimeFinishTurnover > 0
					? balanceAfterTurnoverFinish / totalTimeFinishTurnover
					: 0

			return (
				<div className="flex flex-col">
					<span className="text-sm font-medium">
						฿{balanceAfterTurnoverFinish.toLocaleString()}
					</span>
					<div className="mt-1 flex flex-col gap-1">
						<span className="text-xs text-gray-500">
							เฉลี่ยทั้งหมด: ฿
							{averagePerReceiveTime.toLocaleString(undefined, {
								minimumFractionDigits: 2,
								maximumFractionDigits: 2,
							})}
						</span>
						<span className="text-xs text-gray-500">
							เฉลี่ยหลังทำเทิร์นสำเร็จ: ฿
							{averagePerFinishTime.toLocaleString(undefined, {
								minimumFractionDigits: 2,
								maximumFractionDigits: 2,
							})}
						</span>
					</div>
				</div>
			)
		},
	},
	{
		accessorKey: 'playerTurnoverAmountAfterClearTurnover',
		header: ({ column }) => {
			return (
				<Button
					variant="ghost"
					onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
					className="px-0 hover:bg-transparent"
				>
					ยอดเทิร์นที่ทำได้ในกรณีทำเทิร์นไม่สำเร็จ / เฉลี่ย
					<SortingArrows sortOrder={column.getIsSorted()} />
				</Button>
			)
		},
		cell: ({ row }) => {
			const {
				playerTurnoverAmountAfterClearTurnover,
				totalReceiveTime,
				totalTimeFinishTurnover,
			} = row.original
			const average =
				totalReceiveTime > 0
					? playerTurnoverAmountAfterClearTurnover / totalReceiveTime
					: 0
			const averageFailedTurnover =
				totalReceiveTime > 0 && totalTimeFinishTurnover > 0
					? playerTurnoverAmountAfterClearTurnover /
						(totalReceiveTime - totalTimeFinishTurnover)
					: 0
			return (
				<div className="flex flex-col">
					<span className="text-sm font-medium">
						{playerTurnoverAmountAfterClearTurnover.toLocaleString()}
					</span>
					<span className="text-xs text-gray-500">
						เฉลี่ย:{' '}
						{average.toLocaleString(undefined, {
							minimumFractionDigits: 2,
							maximumFractionDigits: 2,
						})}
					</span>
					<span className="text-xs text-gray-500">
						เฉลี่ยหลังทำเทิร์นไม่สำเร็จ:{' '}
						{averageFailedTurnover.toLocaleString(undefined, {
							minimumFractionDigits: 2,
							maximumFractionDigits: 2,
						})}
					</span>
				</div>
			)
		},
	},
	{
		id: 'details',
		header: 'รายละเอียด',
		cell: ({ row }) => (
			<Button asChild variant="outline">
				<Link
					to={`/operator/get-player-list/player-detail/${row.original.playerId}`}
				>
					ดูรายละเอียดผู้เล่น
				</Link>
			</Button>
		),
	},
]
